//@flow
import * as Immutable from 'immutable';

import { MasterTeam } from 'common/models/flow/MasterTeam';
import type { MasterTeamModelType } from 'common/models/flow/MasterTeam';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';

import {
    validateId,
    validatable,
    validate,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import DataUtils from 'common/utils/DataUtils';
import StringUtils from 'common/utils/StringUtils';
import RegExUtil from 'common/utils/RegExUtil';
import { BusinessUnitId, BusinessUnit } from 'common/models/flow/BusinessUnits';

import I18nHelper from 'common/helpers/I18nHelper';
import { AuditMetadata } from 'common/models/internal/AuditMetadata';
import EntityTypeEnum from 'common/enums/EntityTypeEnum';
import { CityId } from 'common/models/internal/CityId';

const translationsNamespace = ['entityManagement'];

const nsTranslate = I18nHelper.translate.bind(null, translationsNamespace);

export const TeamStatusEnum = new EntityTypeEnum({
    Inactive: 'INACTIVE',
    Active: 'ACTIVE'
});
@validatable
class InternalMasterTeam extends MasterTeam {
    static fromJS(json: MasterTeamModelType): InternalMasterTeam {
        const state: Object = Object.assign({}, json);
        state.businessUnit = state.businessUnit
            ? BusinessUnit.fromJS(state.businessUnit)
            : state.businessUnit;
        state.businessUnitId = state.businessUnitId
            ? BusinessUnitId.fromJS(state.businessUnitId)
            : state.businessUnitId;
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.customProperties = Immutable.Map(state.customProperties);
        state.cityId = state.cityId
            ? CityId.fromJS(state.cityId)
            : state.cityId;
        return new this(Immutable.Map(state));
    }

    @validateAll
    setAllValidationErrors(): this {
        return this;
    }

    getCellValidationMessage({ header, validationData }: Object) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    validateId() {
        return validateId(this.id);
    }

    @validate
    setCode(code: string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    validateCode() {
        const code = this.code || this.id;
        if (StringUtils.isEmpty(code)) {
            return nsTranslate('entityValidationErrors.codeEmpty');
        }
        if (code.trim().length < 2) {
            return nsTranslate('entityValidationErrors.codeLessThan2');
        }
        if (code.trim().length > 32) {
            return nsTranslate('entityValidationErrors.codeGreaterThan32');
        }
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName() {
        if (!this.name || this.name.trim().length === 0) {
            return nsTranslate('teamMasterValidations.nameEmpty');
        }

        if (!this.name || this.name.length > 128) {
            return nsTranslate('teamMasterValidations.nameOverLimit');
        }
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    isHidden(key: string) {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    static defaultInit(): InternalMasterTeam {
        return InternalMasterTeam.fromJS({});
    }

    get businessUnitId(): ?BusinessUnitId {
        return this._state.get('businessUnitId');
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return false;
    }

    get businessUnit(): ?BusinessUnit {
        return this._state.get('businessUnit');
    }

    sanitize(): this {
        const { businessUnitId, cityId } = this;
        let sanitizedTeam = this;
        if (DataUtils.isEmpty(businessUnitId?.businessUnitId)) {
            sanitizedTeam = sanitizedTeam.setBusinessUnitId(null);
        }
        if (DataUtils.isEmpty(cityId?.cityId)) {
            sanitizedTeam = sanitizedTeam.setCityId(null);
        }
        return sanitizedTeam.setBusinessUnit(null);
    }

    defaultSanitize(): this {
        return this.sanitize();
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    get cityId(): ?CityId {
        return this._state.get('cityId');
    }
}

export { InternalMasterTeam as MasterTeam };
